import styled from 'styled-components';

import FeatherIcon from 'feather-icons-react';
import Button from 'components/button/Button';
import Tooltip from 'components/tooltip';

export interface BulkActionBarProps {
  selectedCount: number;
  isAllAssignedSelected?: boolean;
  onEditDeadline: () => void;
  onClearSelection: () => void;
}

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  padding: 10px 14px;
  margin: 30px 0;
  border: 1px solid #a793f6;
  border-radius: 12px;
  background: #f7f5ff;

  @media (max-width: 768px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const SelectionGroup = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  color: #1a2535;
`;

const SelectionText = styled.span`
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
`;

const SelectionHint = styled.span`
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #555770;
`;

const ClearSelectionButton = styled.button`
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 6px;
  background: var(--lilac-dark, #7965c6);
  cursor: pointer;
  font-size: 13px;
  line-height: 16px;
  font-weight: 500;
  color: #ffffff;
  transition: background-color 0.2s ease;

  &:hover {
    background: #6d54cf;
  }

  &:focus-visible {
    outline: 2px solid #a793f6;
    outline-offset: 2px;
  }
`;

const ClearSelectionLabel = styled.span`
  display: inline-flex;
  align-items: center;
`;

const ActionButtonContent = styled.span`
  align-items: center;
  display: flex;
  gap: 10px;
`;

const ActionButtonLabel = styled.span`
  color: var(--black-dark, #1a2535);
  font-size: 16px;
  font-family: Lato;
  font-style: normal;
  font-weight: 700;
  line-height: 23px;
  letter-spacing: 0;
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 58px;
  padding: 6px 10px;
  border-radius: 30px;
  background: var(--lilac-dark, #7965c6);
  color: var(--white, #ffffff);
  font-size: 12px;
  line-height: 14px;
  font-weight: 400;
`;

const EditDeadlinesButton = styled(Button)`
  &&& {
    min-width: 217px;
    height: 44px;
    padding: 12px 24px;
    border-radius: 8px;
    background: transparent;
    border: 1px solid var(--purple-light, #a793f6);
    color: var(--black-dark, #1a2535);
    box-shadow: none;
  }

  &&&:hover,
  &&&:focus,
  &&&:active {
    background: transparent;
    border: 1px solid var(--black-dark, #1a2535);
    color: var(--black-dark, #1a2535);
    box-shadow: none;
  }

  .BulkActionBar__ActionButtonContent {
    display: flex;
  }

  &&&[disabled] {
    background: transparent;
    border: 1px solid var(--gray-300, #d0d2db);
    color: var(--gray-700, #57575c);
  }
`;

const BulkActionBar = ({
  selectedCount,
  isAllAssignedSelected = false,
  onEditDeadline,
  onClearSelection,
}: BulkActionBarProps): JSX.Element => {
  const defaultLabel = `${selectedCount} user${selectedCount !== 1 ? 's' : ''}`;
  const usersLabel = `${defaultLabel} selected`;

  return (
    <Toolbar role="toolbar" aria-label={`Bulk actions for ${selectedCount} selected users`}>
      <SelectionGroup>
        <SelectionText>{usersLabel}</SelectionText>
        {isAllAssignedSelected && <SelectionHint>(all assigned)</SelectionHint>}
        <ClearSelectionButton type="button" onClick={onClearSelection}>
          <FeatherIcon icon="x" size={16} color="#ffffff" aria-hidden="true" />
          <ClearSelectionLabel>Clear selection</ClearSelectionLabel>
        </ClearSelectionButton>
      </SelectionGroup>

      <Tooltip title="Manage user deadlines">
        <EditDeadlinesButton
          className="BulkActionBar__EditDeadlinesButton"
          $secondary
          onClick={onEditDeadline}
        >
          <ActionButtonContent className="BulkActionBar__ActionButtonContent">
            <ActionButtonLabel>Edit deadlines</ActionButtonLabel>
            <CountBadge>{defaultLabel}</CountBadge>
          </ActionButtonContent>
        </EditDeadlinesButton>
      </Tooltip>
    </Toolbar>
  );
};

export default BulkActionBar;
